// src/pages/InformeChequesECheq.jsx
import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import moment from "moment";

import { getCartera } from "../helpers/rutaCarteras";

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const money = (n) => `$ ${fmtARS(n)}`;

const estadoUi = (v) => {
  const s = String(v || "").toUpperCase().trim();
  return s === "ENDOSADO_A_INGRESAR" ? "EMITIDO_A_INGRESAR" : s;
};

const tipoUi = (v) => String(v || "").toUpperCase().trim();

const fecha = (v) => {
  if (!v) return "";
  const m = moment(v);
  return m.isValid() ? m.format("DD/MM/YYYY") : "";
};

const getNro = (r) => r?.nro || r?.nroCheque || r?.idICheq || "";

const getBanco = (r) => r?.bancoEmisor || r?.banco || "";

const getFechaCheque = (r) => r?.fechaCheque || r?.fechaPagoDiferido || "";

const getFechaPago = (r) =>
  r?.fechaPago || r?.fechaLiquidacion || r?.fechaPagoDiferido || "";

const getCobradoA = (r) => r?.cobradoA || r?.librador || r?.emisor || "";

const getCuitCobradoA = (r) =>
  r?.cuitCobradoA || r?.cuitLibrador || r?.cuitEmisor || "";

const getPagadoA = (r) => r?.pagadoA || r?.beneficiario || "";

const getCuitPagadoA = (r) =>
  r?.cuitPagadoA || r?.cuitBeneficiario || "";

const getCliente = (r) =>
  r?.clienteId?.razonsocial || r?.clienteId?.nombre || "";

const getOrigen = (r) => {
  if (r?.ocId?.numero) return `OCR ${r.ocId.numero}`;
  if (r?.opId?.numero) return `OPR ${r.opId.numero}`;
  if (r?.origenTipo) return r.origenTipo;
  return "";
};

const sumImporte = (arr) =>
  arr.reduce((acc, x) => acc + Number(x?.importe || 0), 0);

const byTipo = (arr, tipo) =>
  arr.filter((x) => tipoUi(x.tipo) === tipo);

const isEnCartera = (r) => estadoUi(r.estado) === "EN_CARTERA";

const isEmitido = (r) =>
  ["EMITIDO", "EMITIDO_A_INGRESAR"].includes(estadoUi(r.estado));

const isPagado = (r) => estadoUi(r.estado) === "PAGADO";

const isAnulado = (r) => estadoUi(r.estado) === "ANULADO";

const isOtroEstado = (r) =>
  !isEnCartera(r) && !isEmitido(r) && !isPagado(r) && !isAnulado(r);

const sortByFechaPago = (a, b) => {
  const fa = getFechaPago(a) || getFechaCheque(a) || a?.recibidoEn;
  const fb = getFechaPago(b) || getFechaCheque(b) || b?.recibidoEn;

  const ma = fa ? moment(fa).valueOf() : 0;
  const mb = fb ? moment(fb).valueOf() : 0;

  return ma - mb;
};

function TotalCard({ titulo, valor, subtitulo }) {
  return (
    <Card
      className="border-0 shadow-sm h-100"
      style={{
        borderLeft: "5px solid #0d6efd",
        background: "#f8fbff",
      }}
    >
      <Card.Body className="py-3" style={{ textAlign: "left" }}>
        <div
          className="text-muted"
          style={{
            fontSize: 13,
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          {titulo}
        </div>

        <div
          className="fw-bold"
          style={{
            fontSize: 28,
            lineHeight: 1.1,
            color: "#0d6efd",
            letterSpacing: "-0.5px",
          }}
        >
          {money(valor)}
        </div>

        {subtitulo ? (
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            {subtitulo}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

function TablaDetalle({ titulo, items }) {
  const total = sumImporte(items);

  return (
    <Card className="mt-3 shadow-sm border-0">
      <Card.Body>
        <div
        className="d-flex flex-wrap align-items-center mb-2"
        style={{ gap: 12 }}
        >
        <h5 className="mb-0">{titulo}</h5>

        <span
            style={{
            background: "#e7f1ff",
            color: "#0d6efd",
            border: "1px solid #b6d4fe",
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: 15,
            fontWeight: 700,
            }}
        >
            Total: {money(total)}
        </span>
        </div>

        <div className="table-responsive" style={{ maxHeight: 430, overflow: "auto" }}>
          <Table
            striped
            bordered
            hover
            size="sm"
            className="mb-0"
            style={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <th>Tipo</th>
                <th>Estado</th>
                <th style={{ textAlign: "right" }}>Importe</th>
                <th>Recibido</th>
                <th>Fecha cheque</th>
                <th>Fecha pago</th>
                <th>Nro</th>
                <th>Banco</th>
                <th>Cobrado a</th>
                <th>CUIT cobrado</th>
                <th>Pagado a</th>
                <th>CUIT pagado</th>
                <th>Cliente</th>
                <th>Origen</th>
                <th>Observaciones</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={15} className="text-center">
                    Sin registros.
                  </td>
                </tr>
              ) : (
                items.map((r) => (
                  <tr key={r._id || `${getNro(r)}-${r.tipo}-${r.estado}`}>
                    <td>{tipoUi(r.tipo)}</td>
                    <td>{estadoUi(r.estado)}</td>
                    <td style={{ textAlign: "right" }}>{money(r.importe)}</td>
                    <td>{fecha(r.recibidoEn)}</td>
                    <td>{fecha(getFechaCheque(r))}</td>
                    <td>{fecha(getFechaPago(r))}</td>
                    <td>{getNro(r)}</td>
                    <td>{getBanco(r)}</td>
                    <td>{getCobradoA(r)}</td>
                    <td>{getCuitCobradoA(r)}</td>
                    <td>{getPagadoA(r)}</td>
                    <td>{getCuitPagadoA(r)}</td>
                    <td>{getCliente(r)}</td>
                    <td>{getOrigen(r)}</td>
                    <td>{r?.observaciones || ""}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
            <tr
                style={{
                background: "#e7f1ff",
                color: "#0d6efd",
                fontWeight: "bold",
                }}
            >
                <td colSpan={2}>
                <strong>Total</strong>
                </td>
                <td style={{ textAlign: "right", fontSize: 14 }}>
                <strong>{money(total)}</strong>
                </td>
                <td colSpan={12}></td>
            </tr>
            </tfoot>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}

export default function InformeChequesECheq() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [incluirPagados, setIncluirPagados] = useState(false);
  const [incluirOtros, setIncluirOtros] = useState(false);
  const [incluirAnulados, setIncluirAnulados] = useState(false);

  const consultar = async () => {
    setErr("");
    setLoading(true);

    try {
      const resp = await getCartera();

      const items = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.items)
        ? resp.items
        : Array.isArray(resp?.elementos)
        ? resp.elementos
        : [];

      setData(
        items.map((x) => ({
          ...x,
          estado: estadoUi(x.estado),
          tipo: tipoUi(x.tipo),
        }))
      );
    } catch (e) {
      setData([]);
      setErr(e?.message || "No se pudo cargar el informe.");
    } finally {
      setLoading(false);
    }
  };

  const grupos = useMemo(() => {
    const base = data.filter((x) => {
      if (!incluirAnulados && isAnulado(x)) return false;
      return true;
    });

    const enCartera = base.filter(isEnCartera).sort(sortByFechaPago);
    const emitidos = base.filter(isEmitido).sort(sortByFechaPago);
    const pagados = base.filter(isPagado).sort(sortByFechaPago);
    const otros = base.filter(isOtroEstado).sort(sortByFechaPago);

    return {
      enCartera,
      emitidos,
      pagados,
      otros,

      chequesEnCartera: byTipo(enCartera, "CHEQUE"),
      echeqEnCartera: byTipo(enCartera, "ICHEQ"),

      chequesEmitidos: byTipo(emitidos, "CHEQUE"),
      echeqEmitidos: byTipo(emitidos, "ICHEQ"),

      chequesPagados: byTipo(pagados, "CHEQUE"),
      echeqPagados: byTipo(pagados, "ICHEQ"),

      chequesOtros: byTipo(otros, "CHEQUE"),
      echeqOtros: byTipo(otros, "ICHEQ"),
    };
  }, [data, incluirAnulados]);

  const resumen = useMemo(() => {
    const fila = (nombre, arr) => {
      const cheques = byTipo(arr, "CHEQUE");
      const echeq = byTipo(arr, "ICHEQ");

      return {
        nombre,
        chequeCantidad: cheques.length,
        chequeTotal: sumImporte(cheques),
        echeqCantidad: echeq.length,
        echeqTotal: sumImporte(echeq),
        totalCantidad: arr.length,
        total: sumImporte(arr),
      };
    };

    const filas = [
      fila("EN CARTERA", grupos.enCartera),
      fila("EMITIDOS", grupos.emitidos),
    ];

    if (incluirPagados) filas.push(fila("PAGADOS", grupos.pagados));
    if (incluirOtros) filas.push(fila("OTROS ESTADOS", grupos.otros));

    const visibles = [
      ...grupos.enCartera,
      ...grupos.emitidos,
      ...(incluirPagados ? grupos.pagados : []),
      ...(incluirOtros ? grupos.otros : []),
    ];

    filas.push(fila("TOTAL GENERAL", visibles));

    return filas;
  }, [grupos, incluirPagados, incluirOtros]);

  const totalCartera = sumImporte(grupos.enCartera);
  const totalEmitidos = sumImporte(grupos.emitidos);
  const neto = totalCartera - totalEmitidos;

  const visiblesParaTotales = [
    ...grupos.enCartera,
    ...grupos.emitidos,
    ...(incluirPagados ? grupos.pagados : []),
    ...(incluirOtros ? grupos.otros : []),
    ];

  const totalVisible = sumImporte(visiblesParaTotales);
  const cantidadVisible = visiblesParaTotales.length;
 
  return (
    <Container fluid className="py-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3">
            <div>
              <h4 className="mb-1">Informe de Cheques / eCheq</h4>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Detallado por cartera y emitidos
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap align-items-center">
              <Form.Check
                type="switch"
                label="Ver pagados"
                checked={incluirPagados}
                onChange={(e) => setIncluirPagados(e.target.checked)}
              />

              <Form.Check
                type="switch"
                label="Ver otros estados"
                checked={incluirOtros}
                onChange={(e) => setIncluirOtros(e.target.checked)}
              />

              <Form.Check
                type="switch"
                label="Incluir anulados"
                checked={incluirAnulados}
                onChange={(e) => setIncluirAnulados(e.target.checked)}
              />

              <Button onClick={consultar} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" /> Consultando…
                  </>
                ) : (
                  "Consultar"
                )}
              </Button>
            </div>
          </div>

          {err ? (
            <Alert className="mt-3 mb-0" variant="danger">
              {err}
            </Alert>
          ) : null}
        </Card.Body>
      </Card>

      {data.length === 0 ? (
        <div className="mt-3 text-muted">
          Tocá <b>Consultar</b> para cargar el informe.
        </div>
      ) : (
        <>
          <Row className="mt-3 g-2">
            <Col md={3}>
              <TotalCard
                titulo="Total en cartera"
                valor={totalCartera}
                subtitulo={`${grupos.enCartera.length} registros`}
              />
            </Col>

            <Col md={3}>
              <TotalCard
                titulo="Total emitidos"
                valor={totalEmitidos}
                subtitulo={`${grupos.emitidos.length} registros`}
              />
            </Col>

            <Col md={3}>
              <TotalCard
                titulo="Neto cartera - emitidos"
                valor={neto}
                subtitulo="Control general"
              />
            </Col>

            <Col md={3}>
              <TotalCard
                titulo="Total visible informe"
                valor={totalVisible}
                subtitulo={`${cantidadVisible} registros`}
              />
            </Col>
          </Row>

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Resumen por tipo y estado</h5>

              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Grupo</th>
                      <th style={{ textAlign: "right" }}>Cant. Cheques</th>
                      <th style={{ textAlign: "right" }}>Total Cheques</th>
                      <th style={{ textAlign: "right" }}>Cant. eCheq</th>
                      <th style={{ textAlign: "right" }}>Total eCheq</th>
                      <th style={{ textAlign: "right" }}>Cant. Total</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {resumen.map((r) => {
                    const esTotal = r.nombre === "TOTAL GENERAL";

                    return (
                        <tr
                        key={r.nombre}
                        style={
                            esTotal
                            ? {
                                background: "#e7f1ff",
                                color: "#0d6efd",
                                fontWeight: "bold",
                                fontSize: 13,
                                }
                            : {}
                        }
                        >
                        <td>
                            <strong>{r.nombre}</strong>
                        </td>
                        <td style={{ textAlign: "right" }}>{r.chequeCantidad}</td>
                        <td style={{ textAlign: "right" }}>{money(r.chequeTotal)}</td>
                        <td style={{ textAlign: "right" }}>{r.echeqCantidad}</td>
                        <td style={{ textAlign: "right" }}>{money(r.echeqTotal)}</td>
                        <td style={{ textAlign: "right" }}>{r.totalCantidad}</td>
                        <td style={{ textAlign: "right", fontSize: esTotal ? 14 : 12 }}>
                            <strong>{money(r.total)}</strong>
                        </td>
                        </tr>
                    );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <TablaDetalle
            titulo="CHEQUES EN CARTERA"
            items={grupos.chequesEnCartera}
          />

          <TablaDetalle
            titulo="ECHEQ EN CARTERA"
            items={grupos.echeqEnCartera}
          />

          <TablaDetalle
            titulo="CHEQUES EMITIDOS"
            items={grupos.chequesEmitidos}
          />

          <TablaDetalle
            titulo="ECHEQ EMITIDOS"
            items={grupos.echeqEmitidos}
          />

          {incluirPagados ? (
            <>
              <TablaDetalle
                titulo="CHEQUES PAGADOS"
                items={grupos.chequesPagados}
              />

              <TablaDetalle
                titulo="ECHEQ PAGADOS"
                items={grupos.echeqPagados}
              />
            </>
          ) : null}

          {incluirOtros ? (
            <>
              <TablaDetalle
                titulo="CHEQUES - OTROS ESTADOS"
                items={grupos.chequesOtros}
              />

              <TablaDetalle
                titulo="ECHEQ - OTROS ESTADOS"
                items={grupos.echeqOtros}
              />
            </>
          ) : null}
        </>
      )}
    </Container>
  );
}